import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RecipeDetailProduct } from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  DeleteOutlined,
} from '@mui/icons-material';

import { Image } from '../../../common';

type RecipeCartProductSelectMobileProps<T extends Array<RecipeDetailProduct>> =
  {
    product: T;
    selectedProductIdx: number;
    handleNavigate: (productId: string) => void;
    handleDelete: () => void;
  } & BoxProps;

export function RecipeCartProductSelectMobile<
  T extends Array<RecipeDetailProduct>
>({
  product,
  selectedProductIdx,
  handleNavigate,
  handleDelete,
  ...props
}: RecipeCartProductSelectMobileProps<T>) {
  const isEveryProductSame = product.every((p) => p.id === product[0].id);
  const isPrevousButtonDisabled =
    product.length <= 1 || selectedProductIdx === 0 || isEveryProductSame;
  const isNextButtonDisabled =
    product.length <= 1 ||
    selectedProductIdx === product.length - 1 ||
    isEveryProductSame;

  return (
    <Grid
      templateColumns="repeat(12, 1fr)"
      {...props}
      alignItems="center"
      w="full"
    >
      <GridItem as={VStack} alignItems="flex-start" colSpan={11}>
        <Flex w="full">
          <IconButton
            isDisabled={isPrevousButtonDisabled}
            icon={<Icon as={ChevronLeftOutlined} />}
            aria-label="Prev"
            size="xs"
            bg="transparent"
            color="blue"
            _hover={{
              bg: 'transparent',
            }}
            _disabled={{
              bg: 'transparent',
              color: 'gray.33',
              cursor: 'not-allowed',
            }}
            onClick={() => handleNavigate(product[selectedProductIdx - 1].id)}
          />
          <Box overflow="hidden" position="relative" w="full">
            <Flex
              transition="transform 0.5s"
              transform={`translateX(-${selectedProductIdx * 100}%)`}
              willChange="transform"
              alignItems="center"
            >
              {product.map((product) => {
                const isNotAvailable =
                  product.stock === 0 ||
                  product.stock < product.minimum_order_quantity;
                return (
                  <Text
                    textAlign="center"
                    minW="0px"
                    flex="0 0 100%"
                    fontSize="sm"
                    mt="0.5"
                    textDecoration={isNotAvailable ? 'line-through' : undefined}
                  >
                    <Text as="span" fontWeight="bold">
                      {product.brand_desc}
                    </Text>{' '}
                    {product.title}
                  </Text>
                );
              })}
            </Flex>
          </Box>
          <IconButton
            isDisabled={isNextButtonDisabled}
            icon={<Icon as={ChevronRightOutlined} />}
            aria-label="Prev"
            size="xs"
            bg="transparent"
            color="blue"
            _hover={{
              bg: 'transparent',
            }}
            _disabled={{
              bg: 'transparent',
              color: 'gray.33',
              cursor: 'not-allowed',
            }}
            onClick={() => handleNavigate(product[selectedProductIdx + 1].id)}
          />
        </Flex>
        <Box overflow="hidden" position="relative" w="full">
          <Flex
            transition="transform 0.5s"
            transform={`translateX(-${selectedProductIdx * 100}%)`}
            willChange="transform"
          >
            {product.map((p) => (
              <RecipeCartProductItem product={p} minW="0px" flex="0 0 100%" />
            ))}
          </Flex>
        </Box>
      </GridItem>
      <GridItem>
        <IconButton
          ml="5"
          icon={<Icon as={DeleteOutlined} />}
          aria-label="Prev"
          size="xs"
          bg="transparent"
          color="blue"
          _hover={{
            bg: 'transparent',
          }}
          onClick={handleDelete}
        />
      </GridItem>
    </Grid>
  );
}

type RecipeCartProductItemProps<T extends RecipeDetailProduct> = {
  product: T;
} & FlexProps;

function RecipeCartProductItem<T extends RecipeDetailProduct>({
  product,
  ...props
}: RecipeCartProductItemProps<T>) {
  return (
    <Flex {...props} gap="2" alignItems="center">
      <Box
        flex="0 0 40px"
        position="relative"
        w="40px"
        h="40px"
        border="1px solid"
        borderColor="gray.33"
        rounded="lg"
      >
        <Image
          src={product.product_image_thumbnails?.[0]}
          alt={product.title}
          style={{
            borderRadius: '0.5rem',
            objectFit: 'cover',
          }}
          fill
        />
      </Box>
      <Flex justifyContent="space-between" gap="4" w="full">
        <VStack alignItems="flex-start" spacing="0">
          <Text fontSize="2xs" as="span" color="gray.75">
            Harga Satuan
          </Text>
          <Text fontSize="sm" mt="0">
            {buyerCurrencyFormatter(product.price)}
          </Text>
        </VStack>
        <VStack alignItems="flex-start" spacing="0">
          <Text fontSize="2xs" as="span" color="gray.75">
            qty
          </Text>
          <Text fontSize="sm" mt="0">
            x{product.minimum_order_quantity / 1000}
            <Text as="span" fontSize="2xs" color="gray.75">
              /{product.sales_unit_desc}
            </Text>
          </Text>
        </VStack>
        <VStack alignItems="flex-start" spacing="0">
          <Text fontSize="2xs" as="span" color="gray.75">
            Total Harga
          </Text>
          <Text fontSize="sm" mt="0" fontWeight="semibold">
            {buyerCurrencyFormatter(product.subtotal)}
          </Text>
        </VStack>
      </Flex>
    </Flex>
  );
}
